import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  Broadcast, Buildings, Sparkle,
  Link as LinkIcon, Archive, GraduationCap, Gavel,
  List, SignOut, Lightning, ArrowLeft,
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const NAV_PRIMARY = [
  { to: "/insights",              exact: true, label: "Trend Radar",      icon: Broadcast, sub: "All intelligence" },
  { to: "/insights/suggestions",              label: "Suggested Topics",  icon: Sparkle,   sub: "Hermes research ideas" },
  { to: "/insights/entities",                 label: "Entities",          icon: Buildings, sub: "Companies & orgs" },
];

const NAV_OPERATIONAL = [
  { to: "/insights/sources",                  label: "Source Monitor",          icon: LinkIcon,      sub: "URL & RSS sources" },
  { to: "/insights/vault",                    label: "Vault",                   icon: Archive,       sub: "Bookmarked intelligence" },
];

const NAV_KNOWLEDGE = [
  { to: "/insights/learnings",                label: "Institutional Knowledge", icon: GraduationCap, sub: "Operator-validated facts" },
  { to: "/insights/decisions",                label: "Decisions",               icon: Gavel,         sub: "Strategic decision log" },
];

function NavItem({ item, onClose, inboxCount }) {
  const location = useLocation();
  const Icon = item.icon;
  const isActive = item.exact
    ? location.pathname === item.to
    : location.pathname.startsWith(item.to);
  const showBadge = item.badge && inboxCount > 0;

  return (
    <NavLink
      to={item.to}
      end={item.exact}
      onClick={onClose}
      className={() =>
        `relative flex items-center gap-2.5 px-2.5 py-2 rounded-md transition-all duration-100 group ${
          isActive
            ? "bg-white/[0.1] text-white"
            : "text-slate-300 hover:bg-white/[0.06] hover:text-white"
        }`
      }
    >
      {isActive && (
        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-teal-400 rounded-r-full" />
      )}
      <Icon
        size={15}
        weight={isActive ? "fill" : "regular"}
        className={`shrink-0 ${isActive ? "text-white" : "text-slate-400"}`}
      />
      <div className="min-w-0 flex-1">
        <div className={`text-[13px] font-medium leading-none truncate ${isActive ? "text-white" : "text-slate-300"}`}>
          {item.label}
        </div>
        <div className={`text-[11px] mt-0.5 leading-none truncate ${isActive ? "text-slate-400" : "text-slate-500"}`}>
          {item.sub}
        </div>
      </div>
      {showBadge && (
        <span className="ml-auto shrink-0 min-w-[18px] h-[18px] rounded-full bg-blue-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
          {inboxCount > 99 ? "99+" : inboxCount}
        </span>
      )}
    </NavLink>
  );
}

function NavSection({ label, items, onClose, inboxCount }) {
  return (
    <div>
      <div className="px-2.5 mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
        {label}
      </div>
      <div className="space-y-0.5">
        {items.map((item) => (
          <NavItem key={item.to} item={item} onClose={onClose} inboxCount={inboxCount} />
        ))}
      </div>
    </div>
  );
}

function Sidebar({ onClose }) {
  return (
    <div className="flex flex-col h-full bg-slate-950">
      {/* Back to Hub */}
      <Link
        to="/"
        onClick={onClose}
        className="flex items-center gap-2 px-4 py-3 border-b border-slate-800 text-slate-500 hover:text-slate-300 transition-colors shrink-0"
      >
        <ArrowLeft size={13} />
        <span className="text-[11px] font-medium">Back to Hub</span>
      </Link>

      {/* Logo */}
      <Link
        to="/insights"
        onClick={onClose}
        className="flex items-center gap-2.5 px-4 py-4 border-b border-slate-800 shrink-0"
      >
        <div className="w-6 h-6 rounded bg-white/10 border border-white/10 flex items-center justify-center shrink-0">
          <Lightning weight="fill" className="text-teal-400" size={12} />
        </div>
        <div>
          <div className="font-display text-[13px] font-bold text-white leading-none tracking-tight">
            Hermes
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5 leading-none tracking-wider uppercase">
            Intelligence OS
          </div>
        </div>
      </Link>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        <NavSection label="Workspace"  items={NAV_PRIMARY}     onClose={onClose} inboxCount={0} />
        <NavSection label="Sources"    items={NAV_OPERATIONAL} onClose={onClose} inboxCount={0} />
        <NavSection label="Knowledge"  items={NAV_KNOWLEDGE}   onClose={onClose} inboxCount={0} />
      </nav>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-slate-800 shrink-0">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">
          Coverage
        </div>
        <div className="text-[12px] text-slate-300 flex items-center gap-1.5">
          <span className="font-medium">Australia</span>
          <span className="text-slate-600">·</span>
          <span className="font-medium">Global</span>
        </div>
      </div>
    </div>
  );
}

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const allNav = [...NAV_PRIMARY, ...NAV_OPERATIONAL, ...NAV_KNOWLEDGE];
  const current = allNav.find((n) =>
    n.exact ? location.pathname === n.to : location.pathname.startsWith(n.to)
  );

  const initials = (user?.name || user?.email || "?")
    .split(/[\s@.]/).filter(Boolean).slice(0, 2)
    .map((p) => p[0]?.toUpperCase()).join("");

  return (
    <div className="min-h-screen flex bg-white">
      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex w-56 shrink-0 flex-col bg-slate-950 border-r border-slate-800"
        data-testid="sidebar"
      >
        <Sidebar />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div
            className="absolute inset-0 bg-slate-900/60"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="relative z-10 w-56 bg-slate-950 border-r border-slate-800 flex flex-col">
            <Sidebar onClose={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header
          className="sticky top-0 z-40 bg-white border-b border-slate-200"
          data-testid="topbar"
        >
          <div className="flex items-center justify-between px-5 h-11">
            <div className="flex items-center gap-3">
              <button
                className="md:hidden w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100"
                onClick={() => setMobileOpen(true)}
              >
                <List size={16} />
              </button>
              {current && (
                <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
                  {current.label}
                </span>
              )}
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 transition-colors">
                  <div className="w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center">
                    {initials || "U"}
                  </div>
                  <span className="hidden sm:block text-[13px] text-slate-600 font-medium truncate max-w-[120px]">
                    {user?.name || user?.email}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>
                  <div className="text-[10px] text-slate-400 font-normal uppercase tracking-wider">Signed in as</div>
                  <div className="text-xs font-medium text-slate-900 truncate mt-0.5">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="cursor-pointer text-xs">
                  <SignOut size={13} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 min-h-0">{children}</main>
      </div>
    </div>
  );
}
